import { useState, useEffect, useMemo } from 'react';
import { useSMPNavigate } from './hooks/useSMPNavigate';
import { Button } from '@design-system/Button';
import { NavBar } from '@design-system/NavBar';
import { Input } from '@design-system/Input';
import { ErrorLoadingPage } from '@/components/screens/ErrorHandling/ErrorLoadingPage';
import { Skeleton } from '@design-system/Skeleton';
import { useSMPData } from './hooks/useSMPData';
import { useMaintenanceGuard } from './hooks/useMaintenanceGuard';
import {
  useUpdateDiscount,
  useRemoveDiscount,
} from '@viome/viome-base/src/usecase/SMP/SMPQuery';
import type {
  DiscountsResponse,
  SMPPlanComponent,
  SMPSubscriptionDetail,
} from '@viome/viome-base/src/usecase/SMP/SMP.model';
import {
  isSubscriptionCancelled,
  formatProductName,
  getComponentSupplyLabel,
  getComponentOldSupplyLabel,
  formatBillingPolicySupplyText,
  isComponentizationEligible,
  computeDiscountBreakdown,
  taxDollarsToCents,
} from './utils/smpHelpers';
import { ShimmerImage } from './components';
import { logSMPMutationError } from '@/utils/sentryLogger';
import { SMP_PATH } from '@/constants/paths';
import { useIsComponentizationEnabled } from '@/hooks/useIsComponentizationEnabled';
import { useComponentizationEligibleSkus } from '@/hooks/useComponentizationEligibleSkus';

type Step = 'view' | 'enter' | 'applying' | 'success' | 'remove-confirm';

// The discount endpoints use `validateStatus: 200..<500`, so a 4xx body
// flows through `onSuccess`. The backend returns business-rule errors as a
// JSON object (e.g. `{ error: "..." }`), but a misrouted 404 returns an
// HTML page. Treat anything that isn't a plain object as a hard failure
// so the `applying` step doesn't transition with garbage data.
const isJsonObjectResponse = (data: unknown): data is Record<string, unknown> =>
  data !== null && typeof data === 'object';

const formatDiscountValue = (value: string): string => {
  if (!value) return '';
  return value.endsWith('.0') ? value.slice(0, -2) : value;
};

const formatDiscountLabel = (discount: DiscountsResponse): string => {
  const val = formatDiscountValue(discount.discountValue);
  return discount.discountType === 'percentage'
    ? `${val}% off subscription`
    : `$${val} off subscription`;
};

export const SMPDiscountCodePage = () => {
  const { smpNavigate, smpGoBack } = useSMPNavigate();
  const isComponentizationEnabled = useIsComponentizationEnabled();
  const eligibleSkus = useComponentizationEligibleSkus();
  const {
    subscriptionId,
    subscriptionDetail,
    productComponents,
    customerDiscount,
    customerDiscounts,
    maxAllowedDiscounts,
    showMaintenance,
    isLoading,
    isError,
  } = useSMPData({
    skip: { uiMessages: true, canSkip: true, holdingPattern: true },
  });

  useMaintenanceGuard(showMaintenance);

  const [step, setStep] = useState<Step>('enter');
  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [removingCode, setRemovingCode] = useState<string | null>(null);
  const [localRemovedCodes, setLocalRemovedCodes] = useState<string[]>([]);
  const [userRequestedEnter, setUserRequestedEnter] = useState(false);
  const [lastAppliedCode, setLastAppliedCode] = useState<string | null>(null);

  const activeDiscounts = useMemo(() => {
    return customerDiscounts.filter(d => !localRemovedCodes.includes(d.discountCodeName));
  }, [customerDiscounts, localRemovedCodes]);

  const hasActiveDiscount = activeDiscounts.length > 0;
  const canAddMore = activeDiscounts.length < maxAllowedDiscounts;

  const updateMutation = useUpdateDiscount();
  const removeMutation = useRemoveDiscount();

  const goBack = () => smpGoBack();

  useEffect(() => {
    if (step !== 'applying' || customerDiscounts.length === 0) return;
    const newCodePresent = lastAppliedCode
      ? customerDiscounts.some(
          (d) => d.discountCodeName.toLowerCase() === lastAppliedCode.toLowerCase(),
        )
      : true;
    if (!newCodePresent) return;
    setLocalRemovedCodes([]);
    setUserRequestedEnter(false);
    setStep('success');
  }, [step, customerDiscounts, lastAppliedCode]);

  const handleApply = () => {
    if (!code.trim() || !subscriptionId) return;
    setErrorMessage('');
    const trimmedCode = code.trim();
    updateMutation.mutate(
      { discountCode: trimmedCode, subscriptionId },
      {
        onSuccess: (data: unknown) => {
          if (!isJsonObjectResponse(data)) {
            // e.g. HTML error page from a 404 — don't advance to 'applying'.
            setErrorMessage('Something went wrong. Please try again.');
            return;
          }
          const res = data as { error?: string };
          if (res?.error) {
            setErrorMessage(res.error);
          } else {
            setLastAppliedCode(trimmedCode);
            setCode('');
            setStep('applying');
          }
        },
        onError: (error) => {
          setErrorMessage('Invalid discount code. Please try again.');
          logSMPMutationError(
            'applyDiscountCode',
            error,
            { screen: 'SMPDiscountCodePage', subscriptionId },
            'warn',
          );
        },
      },
    );
  };

  const handleRemove = () => {
    if (!subscriptionId || !removingCode) return;
    removeMutation.mutate(
      { subscriptionId, discountCode: removingCode },
      {
        onSuccess: (data: unknown) => {
          if (!isJsonObjectResponse(data)) {
            // Misrouted 4xx returns HTML — fail safe and don't optimistically remove.
            setErrorMessage('Something went wrong. Please try again.');
            setRemovingCode(null);
            setStep('view');
            return;
          }
          const res = data as { error?: string };
          if (res?.error) {
            setErrorMessage(res.error);
            setRemovingCode(null);
            setStep('view');
            return;
          }
          setLocalRemovedCodes(prev => [...prev, removingCode]);
          setRemovingCode(null);
          setStep(activeDiscounts.length <= 1 ? 'enter' : 'view');
        },
        onError: (error) => {
          setErrorMessage('Failed to remove discount code.');
          setRemovingCode(null);
          setStep('view');
          logSMPMutationError('removeDiscountCode', error, {
            screen: 'SMPDiscountCodePage',
            subscriptionId,
          });
        },
      },
    );
  };

  const handleRemoveRequest = (discountCode: string) => {
    setRemovingCode(discountCode);
    setStep('remove-confirm');
  };

  if (isLoading) {
    return (
      <section className="page-container flex flex-col gap-6">
        <NavBar returnButtonTitle="Back" className="md:-ml-9" onReturnClick={goBack} />
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-4 w-72" />
        <Skeleton className="h-12 w-full max-w-sm" />
      </section>
    );
  }

  if (isError || !subscriptionDetail) {
    return <ErrorLoadingPage variant="light" errorType="error" hideBackground hideNavBar />;
  }

  if (isSubscriptionCancelled(subscriptionDetail.subscription_status)) {
    return (
      <section className="page-container flex flex-col gap-6">
        <NavBar returnButtonTitle="Back" onReturnClick={goBack} className="md:-ml-9" />
        <h1 className="typography-h6 text-primary">Discount Code</h1>
        <p className="typography-body text-secondary">
          Discount codes are not available for cancelled subscriptions.
        </p>
      </section>
    );
  }

  const currentStep = hasActiveDiscount && step === 'enter' && !userRequestedEnter ? 'view' : step;
  const { line_items } = subscriptionDetail;

  // Supply copy differs per entry point: the legacy landing page uses the old
  // "Every 12 orders / 30-day supply" wording, the componentized detail page
  // uses the billing-cadence wording. Resolve from the same componentization
  // gate that decides which page rendered, so a refresh keeps the same copy.
  const isComponentized =
    isComponentizationEnabled &&
    isComponentizationEligible(
      subscriptionDetail.componentization,
      subscriptionDetail.line_items,
      eligibleSkus,
    );

  return (
    <section className="page-container flex flex-col gap-8 pb-24" data-testid="smp-discountCodePage">
      <NavBar returnButtonTitle="Back" onReturnClick={goBack} className="md:-ml-9" />

      {currentStep === 'enter' && (
        <EnterCodeStep
          code={code}
          setCode={setCode}
          errorMessage={errorMessage}
          setErrorMessage={setErrorMessage}
          isApplying={updateMutation.isPending}
          onApply={handleApply}
        />
      )}

      {currentStep === 'applying' && <ApplyingShimmer />}

      {currentStep === 'success' && (
        <SuccessStep
          discount={
            (lastAppliedCode
              && activeDiscounts.find(
                (d) => d.discountCodeName.toLowerCase() === lastAppliedCode.toLowerCase(),
              ))
            || customerDiscount
          }
          onRemove={(discountCode) => handleRemoveRequest(discountCode)}
          onReturn={() => smpNavigate(SMP_PATH, { replace: true })}
        />
      )}

      {currentStep === 'view' && (
        <ViewDiscountStep
          discounts={activeDiscounts}
          lineItems={line_items}
          productComponents={productComponents}
          subscriptionDetail={subscriptionDetail}
          isComponentized={isComponentized}
          canAddMore={canAddMore}
          onRemove={handleRemoveRequest}
          onAddMore={() => { setUserRequestedEnter(true); setStep('enter'); }}
        />
      )}

      {currentStep === 'remove-confirm' && removingCode && (
        <RemoveConfirmStep
          discountName={removingCode}
          isRemoving={removeMutation.isPending}
          onConfirm={handleRemove}
          onCancel={() => {
            setRemovingCode(null);
            setUserRequestedEnter(false);
            setStep(hasActiveDiscount ? 'view' : 'enter');
          }}
        />
      )}
    </section>
  );
};

/* ============ Sub-components ============ */

const CouponCard = ({ discount, onRemove }: { discount: DiscountsResponse; onRemove: (code: string) => void }) => (
  <div
    className="flex items-center justify-between rounded-[5px] border border-dashed border-[#009E82] bg-[#EAF2F1] px-4 py-3"
    data-testid={`smp-discount-couponCard-${discount.discountCodeName}`}
  >
    <div className="flex flex-col gap-0">
      <span className="typography-body text-primary">{discount.discountCodeName}</span>
      <span className="typography-title text-[#009E82]">{formatDiscountLabel(discount)}</span>
    </div>
    <button
      className="typography-body text-error"
      onClick={() => onRemove(discount.discountCodeName)}
      data-testid={`smp-discount-removeBtn-${discount.discountCodeName}`}
    >
      Remove
    </button>
  </div>
);

const ApplyingShimmer = () => (
  <div className="flex flex-col gap-12" data-testid="smp-discount-applying">
    <div className="flex flex-col gap-2">
      <Skeleton className="h-10 w-3/4 max-w-xl" />
      <Skeleton className="h-10 w-1/2 max-w-md" />
    </div>
    <Skeleton className="h-[68px] w-full rounded-[5px]" />
    <Skeleton className="h-12 w-full max-w-sm rounded-full" />
  </div>
);

const EnterCodeStep = ({ code, setCode, errorMessage, setErrorMessage, isApplying, onApply }: {
  code: string; setCode: (v: string) => void; errorMessage: string; setErrorMessage: (v: string) => void; isApplying: boolean; onApply: () => void;
}) => (
  <div className="flex flex-col gap-12" data-testid="smp-discount-enterStep">
    <h1 className="text-[40px] font-normal leading-[1.15] text-primary">Discount Code</h1>
    <p className="typography-h2 text-secondary font-light">Enter a discount code to apply to your subscription.</p>
    <div className="flex flex-col gap-2 max-w-sm">
      <label className="typography-caption1 text-tertiary">Discount Code</label>
      <Input
        value={code}
        onChange={(e) => { setCode(e.target.value); setErrorMessage(''); }}
        placeholder="Enter code"
        disabled={isApplying}
        data-invalid={!!errorMessage || undefined}
        data-testid="smp-discount-input"
      />
      {errorMessage && <p className="typography-caption2 text-error" data-testid="smp-discount-error">{errorMessage}</p>}
    </div>
    <div className="max-w-sm">
      <Button onClick={onApply} disabled={!code.trim() || isApplying} className="w-full" data-testid="smp-discount-applyBtn">
        {isApplying ? 'Applying...' : 'Apply'}
      </Button>
    </div>
  </div>
);

const SuccessStep = ({ discount, onRemove, onReturn }: {
  discount: DiscountsResponse | null; onRemove: (code: string) => void; onReturn: () => void;
}) => (
  <div className="flex flex-col gap-12" data-testid="smp-discount-successStep">
    <h1 className="text-[40px] font-normal leading-[1.15] text-primary">
      Discount code{'\n'}successfully applied!
    </h1>
    {discount && <CouponCard discount={discount} onRemove={onRemove} />}
    <div className="max-w-sm">
      <Button onClick={onReturn} className="w-full" data-testid="smp-discount-returnBtn">Return to My Account</Button>
    </div>
  </div>
);

type LineItem = { id: number; image: string; product_name: string; title?: string; price: number };

type DiscountDisplayRow = {
  key: string | number;
  image: string;
  title: string;
  supply: string;
  priceCents: number;
};

const ViewDiscountStep = ({
  discounts,
  lineItems,
  productComponents,
  subscriptionDetail,
  isComponentized,
  canAddMore,
  onRemove,
  onAddMore,
}: {
  discounts: DiscountsResponse[];
  lineItems: LineItem[];
  productComponents: SMPPlanComponent[];
  subscriptionDetail: SMPSubscriptionDetail;
  isComponentized: boolean;
  canAddMore: boolean;
  onRemove: (code: string) => void;
  onAddMore: () => void;
}) => {
  const formatPrice = (cents: number) => `$${(cents / 100).toFixed(2)}`;
  const subTotalCents = lineItems.reduce((sum, item) => sum + item.price, 0);

  const discountBreakdown = computeDiscountBreakdown(discounts, subTotalCents);

  const totalDiscountCents = discountBreakdown.reduce((sum, d) => sum + d.amountCents, 0);
  const discountedSubtotalCents = Math.max(0, subTotalCents - totalDiscountCents);

  const taxCents = taxDollarsToCents(subscriptionDetail.transaction_details?.tax ?? 0);
  const showTaxAmount = taxCents > 0;
  const totalCents = showTaxAmount ? discountedSubtotalCents + taxCents : discountedSubtotalCents;

  const perItemCents = lineItems.length > 0
    ? Math.round(subTotalCents / lineItems.length)
    : 0;
  
  const billingPolicySupply = formatBillingPolicySupplyText(subscriptionDetail.billingPolicy);

  const displayRows: DiscountDisplayRow[] =
    productComponents.length > 0
      ? productComponents.map((c, i) => ({
          key: c.product_id,
          image: c.image,
          title: formatProductName(c.title),
          supply: isComponentized
            ? billingPolicySupply ?? getComponentSupplyLabel(c)
            : getComponentOldSupplyLabel(c),
          priceCents: lineItems[i]?.price ?? perItemCents,
        }))
      : lineItems.map((item) => ({
          key: item.id,
          image: item.image,
          title: formatProductName(item.title || item.product_name),
          supply: isComponentized
            ? billingPolicySupply ?? 'monthly'
            : '30-day supply',
          priceCents: item.price,
        }));

  return (
    <div className="flex flex-col gap-12" data-testid="smp-discount-viewStep">
      <h1 className="text-[40px] font-normal leading-[1.15] text-primary">Discount Code</h1>

      <div className="flex flex-col gap-3 mt-4">
        <div className="flex items-center justify-between">
          <p className="typography-h2 text-primary">Active Discount{discounts.length > 1 ? 's' : ''}</p>
          {canAddMore && (
            <button
              onClick={onAddMore}
              className="rounded-full bg-success px-5 py-2.5 typography-body font-medium text-white hover:opacity-90 transition-opacity"
              data-testid="smp-discount-addMoreBtn"
            >
              + Add Discount Code
            </button>
          )}
        </div>
        {discounts.map((d) => (
          <CouponCard key={d.discountCodeName} discount={d} onRemove={onRemove} />
        ))}
      </div>

      <div className="mt-6 flex flex-col gap-6 rounded-[20px] bg-background py-6 pl-5 pr-4 shadow-none" data-testid="smp-discount-priceBreakdown">
        {displayRows.map((row) => (
          <div key={row.key} className="flex items-center gap-5 px-0">
            <ShimmerImage
              src={row.image}
              alt={row.title}
              loading="lazy"
              containerClassName="relative size-[72px] shrink-0 self-center overflow-hidden rounded-[12px] bg-[#f5f9f9]"
              className="size-full object-cover"
            />
            <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
              <span className="typography-body text-primary">{row.title}</span>
              <span className="typography-caption2 text-secondary">{row.supply}</span>
            </div>
            <span className="typography-body text-primary shrink-0">{formatPrice(row.priceCents)}</span>
          </div>
        ))}

        <hr className="h-px w-full border-0 bg-black/20" />

        <div className="flex w-full flex-col gap-6 items-end" data-testid="smp-discount-priceSummary">
          <PriceRow label="SUB-TOTAL" value={formatPrice(subTotalCents)} />
          {discountBreakdown.map(({ code: discCode, amountCents }, idx) => (
            <DiscountRow
              key={discCode}
              label={idx === 0 ? 'DISCOUNTS' : undefined}
              code={discCode}
              value={`-${formatPrice(amountCents)}`}
              testId={`smp-discount-row-${discCode}`}
            />
          ))}
          <PriceRow
            label="TAX"
            value={showTaxAmount ? formatPrice(taxCents) : 'Calculated at checkout'}
          />
          <hr className="h-px w-full max-w-xs border-0 bg-black/20" />
          <div className="flex w-full max-w-xs justify-between items-baseline">
            <span className="typography-body uppercase text-[#727484]">Total</span>
            <span className="typography-h3 text-primary" data-testid="smp-discount-total">{formatPrice(totalCents)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

const PriceRow = ({ label, value, testId }: { label: string; value: string; testId?: string }) => (
  <div className="flex w-full max-w-xs justify-between gap-4" data-testid={testId}>
    <span className="typography-caption1 uppercase text-[#727484]">{label}</span>
    <span className="typography-body text-primary text-end">{value}</span>
  </div>
);

const DiscountRow = ({ label, code, value, testId }: { label?: string; code: string; value: string; testId?: string }) => (
  <div className="grid w-full max-w-xs grid-cols-3 items-center gap-2" data-testid={testId}>
    <span className="typography-caption1 uppercase text-[#727484]">{label ?? ''}</span>
    <span className="typography-body text-[#146464] text-start">{code}</span>
    <span className="typography-body text-primary text-end">{value}</span>
  </div>
);

const RemoveConfirmStep = ({ discountName, isRemoving, onConfirm, onCancel }: {
  discountName: string; isRemoving: boolean; onConfirm: () => void; onCancel: () => void;
}) => (
  <div className="flex flex-col gap-12" data-testid="smp-discount-removeConfirmStep">
    <h1 className="text-[40px] font-normal leading-[1.15] text-primary">Remove Discount</h1>
    <div className="flex flex-col gap-6">
      <p className="typography-h3 text-primary font-medium">
        Are you sure you want to remove the discount code {discountName}?
      </p>
      <p className="typography-body text-primary">You may not be able to apply this offer again.</p>
    </div>
    <div className="flex flex-col gap-4 max-w-sm">
      <Button onClick={onConfirm} disabled={isRemoving} className="w-full" data-testid="smp-discount-confirmRemoveBtn">
        {isRemoving ? 'Removing...' : 'Yes, remove discount code'}
      </Button>
      <Button variant="secondary" onClick={onCancel} disabled={isRemoving} className="w-full" data-testid="smp-discount-cancelRemoveBtn">
        No, don&apos;t remove
      </Button>
    </div>
  </div>
);
